import type { SupabaseClient } from "@supabase/supabase-js";

// Shared, IdP-agnostic account/workspace context for every SecOps surface (console, mobile,
// guard-web). The ONLY account-listing path is Codex's self-scoped public.my_accounts view:
// active memberships for normal users, plus every tenant account for platform superadmins, as
// account_id + account_name + role - no membership ids, user ids, statuses, or timestamps.
//
// These functions are PURE: resolution takes the stored selection as an argument and never
// reads window/localStorage/AsyncStorage. Each surface owns its own persistence and passes the
// stored value in (console: localStorage; mobile: AsyncStorage), using ACCOUNT_SELECTION_KEY so
// the key string stays identical across surfaces.

export interface MyAccount {
  accountId: string;
  accountName: string;
  role: string;
}

/** Columns-safety allowlist - the switcher selects exactly these, nothing more. */
export const MY_ACCOUNTS_COLUMNS = "account_id, account_name, role";

/** Persistence key for the user's chosen workspace - identical on every surface. */
export const ACCOUNT_SELECTION_KEY = "secops:selected-account";

const ACCOUNT_LIMIT = 50;

/** The caller's workspaces, by name. Throws safe (never leaks the underlying error). */
export async function fetchMyAccounts(client: SupabaseClient): Promise<MyAccount[]> {
  const { data, error } = await client
    .from("my_accounts")
    .select(MY_ACCOUNTS_COLUMNS)
    .order("account_name", { ascending: true })
    .limit(ACCOUNT_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load your workspaces.");
  }
  const accounts: MyAccount[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const accountId = typeof raw.account_id === "string" ? raw.account_id : null;
    if (accountId === null) continue;
    accounts.push({
      accountId,
      accountName:
        typeof raw.account_name === "string" && raw.account_name.length > 0 ? raw.account_name : "Workspace",
      role: typeof raw.role === "string" ? raw.role : "member",
    });
  }
  return accounts;
}

/**
 * True when a stored selection is set but no longer matches any current membership. Surfaces
 * use this to decide whether to drop their persisted selection - the resolver itself stays pure.
 */
export function isStoredSelectionStale(
  accounts: readonly MyAccount[],
  storedSelection: string | null,
): boolean {
  return storedSelection !== null && !accounts.some((account) => account.accountId === storedSelection);
}

/**
 * Resolve the effective account id. Priority:
 *   1. the persisted selection IF it is still one of the caller's memberships,
 *   2. the runtime-config default IF the caller is a member of it,
 *   3. the first membership,
 *   4. the config fallback (when memberships are empty or unavailable).
 *
 * Pure: stale selections are ignored, not cleared here. Callers detect staleness with
 * isStoredSelectionStale and clear their own storage.
 */
export function resolveEffectiveAccount(
  configAccountId: string,
  accounts: readonly MyAccount[],
  storedSelection: string | null,
): string {
  if (storedSelection !== null && accounts.some((account) => account.accountId === storedSelection)) {
    return storedSelection;
  }
  if (accounts.length > 0 && accounts.some((account) => account.accountId === configAccountId)) {
    return configAccountId;
  }
  if (accounts.length > 0) {
    return accounts[0]!.accountId;
  }
  return configAccountId;
}

/** Human-readable membership role for workspace chrome. */
export function formatAccountRoleLabel(role: string): string {
  switch (role) {
    case "admin":
      return "Admin";
    case "supervisor":
      return "Supervisor";
    case "superadmin":
      return "Superadmin";
    case "platform_superadmin":
      return "Founder access";
    case "member":
      return "Member";
    case "guard":
      return "Guard";
    case "support":
      return "Support";
    default:
      return role.length > 0 ? role.charAt(0).toUpperCase() + role.slice(1) : "Member";
  }
}

/** True when the workspace is reachable only via platform founder access (no tenant membership). */
export function isPlatformOnlyWorkspaceRole(role: string): boolean {
  return role === "platform_superadmin";
}

/** Picker / profile CTA for a workspace row. Founder-only rows stay selectable. */
export function formatWorkspacePickerActionLabel(role: string): string {
  return isPlatformOnlyWorkspaceRole(role) ? "Enter as founder" : "Use";
}

/** Subcopy under the workspace name for founder-only rows. */
export function formatWorkspacePickerRoleDetail(role: string, suggested: boolean): string {
  const base = isPlatformOnlyWorkspaceRole(role)
    ? "Founder access · field work joins as Admin"
    : formatAccountRoleLabel(role);
  return suggested ? `${base} · suggested` : base;
}

/** Disambiguate duplicate workspace names in the account switcher. */
export function formatWorkspacePickerLabel(account: MyAccount, accounts: readonly MyAccount[]): string {
  const nameCollisions = accounts.filter((row) => row.accountName === account.accountName).length;
  if (nameCollisions <= 1) return account.accountName;
  return `${account.accountName} (${formatAccountRoleLabel(account.role)})`;
}
